import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Alert, Chip, IconButton, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Service {
  id: number;
  name: string;
  category: string;
  description: string;
  base_price: number;
  duration_minutes: number;
  tax_percentage: number;
  max_discount_percentage: number;
}

const empty: Partial<Service> = {
  name: '', category: '', description: '', base_price: 0,
  duration_minutes: 30, tax_percentage: 0, max_discount_percentage: 100,
};

const ServicesPage = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [form, setForm] = useState<Partial<Service>>({ ...empty });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Service | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchServices = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: Service[] }>(API_ENDPOINTS.SERVICES.LIST);
      setServices(res.data || []);
    } catch {
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServices(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...empty }); setFormError(null); setDialogOpen(true); };
  const openEdit = (s: Service) => { setEditing(s); setForm({ ...s }); setFormError(null); setDialogOpen(true); };
  const closeDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!form.name?.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await api.put(API_ENDPOINTS.SERVICES.UPDATE(editing.id), form);
      } else {
        await api.post(API_ENDPOINTS.SERVICES.CREATE, form);
      }
      closeDialog();
      fetchServices();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setFormError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`${API_ENDPOINTS.SERVICES.DETAIL(deleteTarget.id)}`);
      setDeleteTarget(null);
      fetchServices();
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  const filtered = services.filter(s =>
    `${s.name} ${s.category || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Services</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Service</Button>
      </Box>

      <TextField
        label="Search" placeholder="Name or category..."
        value={search} onChange={e => setSearch(e.target.value)}
        size="small" sx={{ mb: 2, width: 300 }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Duration (min)</TableCell>
                <TableCell align="right">Tax %</TableCell>
                <TableCell width={100} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">No services found</TableCell></TableRow>
              ) : filtered.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.category ? <Chip label={s.category} size="small" /> : '—'}</TableCell>
                  <TableCell align="right">${(s.base_price || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">{s.duration_minutes}</TableCell>
                  <TableCell align="right">{s.tax_percentage}%</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(s)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Service' : 'New Service'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Name *" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Category" value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} fullWidth placeholder="e.g. Consultation, Surgery, Dental" />
            <TextField label="Description" value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            <Box display="flex" gap={2}>
              <TextField label="Base Price ($)" type="number" value={form.base_price ?? 0} onChange={e => setForm(f => ({ ...f, base_price: parseFloat(e.target.value) || 0 }))} fullWidth inputProps={{ min: 0, step: 0.01 }} />
              <TextField label="Duration (min)" type="number" value={form.duration_minutes ?? 30} onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 30 }))} fullWidth inputProps={{ min: 0 }} />
            </Box>
            <Box display="flex" gap={2}>
              <TextField label="Tax %" type="number" value={form.tax_percentage ?? 0} onChange={e => setForm(f => ({ ...f, tax_percentage: parseFloat(e.target.value) || 0 }))} fullWidth inputProps={{ min: 0, max: 100, step: 0.5 }} />
              <TextField label="Max Discount %" type="number" value={form.max_discount_percentage ?? 100} onChange={e => setForm(f => ({ ...f, max_discount_percentage: parseFloat(e.target.value) || 0 }))} fullWidth inputProps={{ min: 0, max: 100, step: 1 }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Service</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicesPage;
